const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
const getToken = () => localStorage.getItem("token");

const authorizedHeaders = (withJson = false) => ({
    ...(withJson ? { "Content-Type": "application/json" } : {}),
    Authorization: `Bearer ${getToken()}`,
});

const parseResponse = async (response, action) => {
    if (!response.ok) {
        let detail = "";
        try {
            const body = await response.json();
            detail = body?.detail ? `: ${body.detail}` : "";
        } catch {
            // The status code is still enough to report a useful error.
        }
        const error = new Error(`${action} (${response.status})${detail}`);
        error.status = response.status;
        throw error;
    }
    return response.status === 204 ? null : response.json();
};

export const createNewThread = async () => {
    const response = await fetch(`${API_URL}/api/new`, {
        method: "POST",
        headers: authorizedHeaders(),
    });
    return parseResponse(response, "Failed to create conversation");
};

export const fetchThread = async (threadId) => {
    if (!threadId) return null;

    try {
        const response = await fetch(`${API_URL}/api/threads/${threadId}`, {
            headers: authorizedHeaders(),
        });
        return await parseResponse(response, "Failed to fetch conversation");
    } catch (error) {
        console.error(error);
        return null;
    }
};

export const postMessage = async (threadId, message) => {
    if (!threadId || !message) {
        throw new Error("Conversation ID and message are required");
    }

    const response = await fetch(`${API_URL}/api/threads/${threadId}`, {
        method: "POST",
        headers: authorizedHeaders(true),
        body: JSON.stringify({ content: message }),
    });
    return parseResponse(response, "Failed to send message");
};

const delay = (milliseconds) =>
    new Promise((resolve) => window.setTimeout(resolve, milliseconds));

export const recoverCompletedReply = async (
    threadId,
    knownMessageIds,
    attempts = 10,
    intervalMilliseconds = 2000
) => {
    for (let attempt = 0; attempt < attempts; attempt += 1) {
        const data = await fetchThread(threadId);
        const hasNewAssistantReply = data?.messages?.some(
            (message) =>
                message.role === "assistant" &&
                !knownMessageIds.has(message.id)
        );

        if (hasNewAssistantReply) {
            return data;
        }

        if (attempt < attempts - 1) {
            await delay(intervalMilliseconds);
        }
    }

    return null;
};

export const fetchUserThreads = async () => {
    try {
        const response = await fetch(`${API_URL}/threads/`, {
            headers: authorizedHeaders(),
        });
        return await parseResponse(response, "Failed to fetch conversations");
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const updateThreadTitle = async (threadId, newTitle) => {
    const response = await fetch(`${API_URL}/threads/${threadId}`, {
        method: "PATCH",
        headers: authorizedHeaders(true),
        body: JSON.stringify({ title: newTitle }),
    });
    return parseResponse(response, "Failed to update title");
};

export const deleteThread = async (threadId) => {
    const response = await fetch(`${API_URL}/threads/${threadId}`, {
        method: "DELETE",
        headers: authorizedHeaders(),
    });
    return parseResponse(response, "Failed to delete conversation");
};
